import { useState, useEffect, useCallback, useMemo } from 'react'
import { Toast, Popup, Picker } from 'react-vant'
import { ArrowLeft } from '@react-vant/icons'
import { useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { getPhysicalTestExportPreview, downloadPhysicalTestExcel } from '../../api/physicalTest'
import { getCurrentAcademicYear, getAcademicYears, getAllClasses } from '../../api/academic'
import { isAdmin, isStaff, canManagePhysicalTest } from '../../utils/permissions'

const GRADE_OPTIONS = [
  '全部年级', '一年级', '二年级', '三年级', '四年级', '五年级', '六年级', '七年级', '八年级', '九年级'
]

const STATUS_FILTERS = [
  { label: '全部学生', value: 'all' },
  { label: '已完成体测', value: 'tested' },
  { label: '待补测/缺考', value: 'untested' },
]

const TEMPLATE_OPTIONS = [
  {
    type: 'national',
    title: '教育部国家数据库直报表',
    badge: '国家标规',
    tagColor: 'bg-[#056b4f] text-white',
    desc: '严格符合《国家学生体质健康标准》上报格式，含学校代码、教育ID、各项实测标准值校验。',
    recommend: '推荐首选',
    filenamePrefix: '教育部体测数据直报表',
  },
  {
    type: 'summary',
    title: '综合成绩分析与换算大表',
    badge: '全校总览',
    tagColor: 'bg-[#7c3aed] text-white',
    desc: '包含学生基本信息、各单项原始实测值、项目折算标准分、附加分、学年总分及综合等级。',
    recommend: '全项明细',
    filenamePrefix: '体测成绩综合汇总分析大表',
  },
  {
    type: 'makeup',
    title: '待补测 / 缺测人员清册',
    badge: '精准清查',
    tagColor: 'bg-[#ea580c] text-white',
    desc: '精准筛查尚未完成全部测试项目或总评成绩不及格的学生名录，方便组织第二轮补测。',
    recommend: '组织补测',
    filenamePrefix: '待补测与异常学生名单',
  },
]

export default function PhysicalTestExport() {
  const navigate = useNavigate()
  const { userInfo } = useSelector(state => state.user)

  // 权限校验
  const hasAccess = isAdmin(userInfo) || isStaff(userInfo) || canManagePhysicalTest(userInfo)

  // 筛选状态
  const [academicYears, setAcademicYears] = useState([])
  const [selectedYear, setSelectedYear] = useState('')
  const [showYearPicker, setShowYearPicker] = useState(false)

  const [allClasses, setAllClasses] = useState([])
  const [selectedGrade, setSelectedGrade] = useState('全部年级')
  const [showGradePicker, setShowGradePicker] = useState(false)

  const [selectedClassId, setSelectedClassId] = useState(null)
  const [showClassPicker, setShowClassPicker] = useState(false)

  const [statusFilter, setStatusFilter] = useState('all')

  // 选中的导出模板
  const [selectedTemplate, setSelectedTemplate] = useState('national')

  // 预览与导出状态
  const [loadingPreview, setLoadingPreview] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [previewResult, setPreviewResult] = useState({
    total_records: 0,
    statistics: null,
    data: [],
  })

  // 1. 初始化筛选项
  useEffect(() => {
    const initData = async () => {
      try {
        const [currRes, yearsRes, classesRes] = await Promise.allSettled([
          getCurrentAcademicYear(),
          getAcademicYears(),
          getAllClasses({ page_size: 1000 }),
        ])

        let yearList = []
        if (yearsRes.status === 'fulfilled' && yearsRes.value) {
          const val = yearsRes.value
          if (Array.isArray(val)) yearList = val
          else if (val.options) yearList = val.options.map(o => String(o.value || o))
          else if (val.years) yearList = val.years
        }

        if (!yearList || yearList.length === 0) {
          const now = new Date()
          const yr = now.getFullYear()
          yearList = [now.getMonth() >= 7 ? `${yr}-${yr + 1}` : `${yr - 1}-${yr}`]
        }
        setAcademicYears(yearList)

        let activeYear = localStorage.getItem('current_academic_year')
        if (!activeYear || !yearList.includes(activeYear)) {
          if (currRes.status === 'fulfilled' && currRes.value?.current_academic_year) {
            activeYear = currRes.value.current_academic_year
          } else {
            activeYear = yearList[0]
          }
        }
        setSelectedYear(activeYear)

        if (classesRes.status === 'fulfilled' && classesRes.value) {
          const list = classesRes.value.items || classesRes.value.data || []
          setAllClasses(list)
        }
      } catch (err) {
        console.warn('初始化导出筛选条件失败:', err)
      }
    }
    initData()
  }, [])

  // 联动年级下的班级列表
  const filteredClasses = useMemo(() => {
    if (!selectedGrade || selectedGrade === '全部年级') {
      return allClasses
    }
    return allClasses.filter(c => c.grade === selectedGrade)
  }, [allClasses, selectedGrade])

  // 2. 拉取导出预览数据与统计
  const fetchPreviewData = useCallback(async () => {
    if (!selectedYear) return
    setLoadingPreview(true)
    try {
      const params = {
        academic_year: selectedYear,
        status_filter: statusFilter,
      }
      if (selectedGrade && selectedGrade !== '全部年级') {
        params.grade = selectedGrade
      }
      if (selectedClassId) {
        params.class_id = selectedClassId
      }

      const res = await getPhysicalTestExportPreview(params)
      if (res) {
        setPreviewResult({
          total_records: res.total_records || (res.data ? res.data.length : 0),
          statistics: res.statistics || null,
          data: res.data || [],
        })
      }
    } catch (err) {
      console.error('拉取体测导出预览失败:', err)
    } finally {
      setLoadingPreview(false)
    }
  }, [selectedYear, selectedGrade, selectedClassId, statusFilter])

  useEffect(() => {
    if (selectedYear) {
      fetchPreviewData()
    }
  }, [fetchPreviewData, selectedYear])

  // 切换年级联动重置班级
  const handleGradeSelect = (g) => {
    setSelectedGrade(g)
    setSelectedClassId(null)
  }

  // 3. 执行 Excel 文件下载
  const handleDownload = async () => {
    if (exporting) return
    setExporting(true)
    try {
      const params = {
        academic_year: selectedYear,
        export_type: selectedTemplate,
        status_filter: statusFilter,
      }
      if (selectedGrade && selectedGrade !== '全部年级') {
        params.grade = selectedGrade
      }
      if (selectedClassId) {
        params.class_id = selectedClassId
      }

      const targetTpl = TEMPLATE_OPTIONS.find(t => t.type === selectedTemplate)
      const gradeText = selectedGrade !== '全部年级' ? `_${selectedGrade}` : ''
      const classText = selectedClassId ? `_${allClasses.find(c => c.id === selectedClassId)?.class_name || ''}` : ''
      const defaultFilename = `${selectedYear}学年${gradeText}${classText}_${targetTpl?.filenamePrefix || '体测报表'}.xlsx`

      const blob = await downloadPhysicalTestExcel(params)
      if (blob) {
        const url = window.URL.createObjectURL(new Blob([blob]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', defaultFilename)
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(url)

        Toast.success('报表导出成功，已开始下载！')
      } else {
        Toast.fail('导出未返回有效文件')
      }
    } catch (error) {
      console.error('导出体测报表失败:', error)
      Toast.fail(error.response?.data?.detail || '导出失败，网络连接超时或服务器繁忙')
    } finally {
      setExporting(false)
    }
  }

  const stats = previewResult.statistics
  const totalCount = previewResult.total_records || (stats?.total_students) || 0
  const avgScore = stats?.average_score ? Number(stats.average_score).toFixed(1) : '-'
  const rawExcGoodRate = stats?.excellent_good_rate ?? stats?.good_excellent_rate
  const excellentRate = rawExcGoodRate !== undefined
    ? `${Number(rawExcGoodRate).toFixed(2)}%`
    : (stats?.excellent_rate !== undefined && stats?.good_rate !== undefined
      ? `${(Number(stats.excellent_rate) + Number(stats.good_rate)).toFixed(2)}%`
      : (stats?.excellent_rate !== undefined ? `${Number(stats.excellent_rate).toFixed(2)}%` : '-'))
  const passRate = stats?.pass_rate !== undefined ? `${stats.pass_rate}%` : '-'

  return (
    <div className="min-h-screen bg-[#f8fafc] text-[#0f172a] pb-28 font-sans select-none">
      {/* 顶栏导航 (星翼云蓝阶页眉) */}
      <div
        className="sticky top-0 z-30 px-4 pt-3 pb-3.5 shadow-sm"
        style={{ background: 'linear-gradient(135deg, #055e43 0%, #076f51 50%, #0c8c68 100%)' }}
      >
        <div className="flex items-center justify-between">
          <button
            onClick={() => navigate(-1)}
            className="w-9 h-9 rounded-full bg-white/20 active:bg-white/30 flex items-center justify-center text-white transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-white" />
          </button>

          <div className="text-center">
            <h1 className="text-[17.5px] font-bold text-white tracking-wide leading-tight">体测成绩导出</h1>
            <p className="text-[11.5px] text-white/80 font-normal mt-0.5">教育部官方模板 · 汇总分析 · 补测清册</p>
          </div>

          <div className="w-9 h-9" />
        </div>
      </div>

      <div className="px-3.5 pt-3 space-y-3.5 max-w-lg mx-auto">
        {/* 1. 筛选条件控制台 (统一采用零滚动条下拉选择器设计) */}
        <div className="bg-white rounded-2xl p-4 shadow-sm border border-[#e2e8f0] space-y-3">
          {/* 学年选择 */}
          <div className="flex items-center justify-between pb-2.5 border-b border-[#f1f5f9]">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-full bg-[#e8f6f0] flex items-center justify-center text-[#056b4f]">
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                  <line x1="16" y1="2" x2="16" y2="6"></line>
                  <line x1="8" y1="2" x2="8" y2="6"></line>
                  <line x1="3" y1="10" x2="21" y2="10"></line>
                </svg>
              </div>
              <span className="text-[14px] font-bold text-[#0f172a]">当前学年</span>
            </div>

            <button
              type="button"
              onClick={() => setShowYearPicker(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-[#f0fdf4] border border-[#a7f3d0] text-[#056b4f] text-[12.5px] font-bold active:bg-[#e8f6f0] transition-colors"
            >
              <span>{selectedYear ? `${selectedYear} 学年` : '选择学年'}</span>
              <span className="text-[10px]">▼</span>
            </button>
          </div>

          {/* 范围限定 (年级与班级双列下拉选择器，无任何横向滚动条) */}
          <div>
            <div className="text-[12px] font-semibold text-[#64748b] mb-1.5">限定范围 (年级与班级)</div>
            <div className="grid grid-cols-2 gap-2">
              {/* 年级下拉选择器 */}
              <button
                type="button"
                onClick={() => setShowGradePicker(true)}
                className={`flex items-center justify-between px-3 py-2 rounded-xl border text-[13px] transition-all ${
                  selectedGrade !== '全部年级'
                    ? 'bg-[#f0fdf4] border-[#056b4f] text-[#056b4f] font-bold'
                    : 'bg-[#f8fafc] border-[#e2e8f0] text-[#334155] font-medium'
                }`}
              >
                <div className="flex items-center gap-1 truncate">
                  <span className="text-[11px] text-[#94a3b8]">年级:</span>
                  <span className="truncate">{selectedGrade}</span>
                </div>
                <span className="text-[10px] text-[#94a3b8] ml-1 shrink-0">▼</span>
              </button>

              {/* 班级下拉选择器 */}
              <button
                type="button"
                onClick={() => setShowClassPicker(true)}
                className={`flex items-center justify-between px-3 py-2 rounded-xl border text-[13px] transition-all ${
                  selectedClassId !== null
                    ? 'bg-[#f0fdf4] border-[#056b4f] text-[#056b4f] font-bold'
                    : 'bg-[#f8fafc] border-[#e2e8f0] text-[#334155] font-medium'
                }`}
              >
                <div className="flex items-center gap-1 truncate">
                  <span className="text-[11px] text-[#94a3b8]">班级:</span>
                  <span className="truncate">
                    {selectedClassId
                      ? (allClasses.find(c => c.id === selectedClassId)?.class_name || '已选班级')
                      : '全部班级'}
                  </span>
                </div>
                <span className="text-[10px] text-[#94a3b8] ml-1 shrink-0">▼</span>
              </button>
            </div>
          </div>

          {/* 导出范围筛选 */}
          <div>
            <div className="text-[12px] font-semibold text-[#64748b] mb-1.5">学生范围</div>
            <div className="grid grid-cols-3 gap-2">
              {STATUS_FILTERS.map(st => {
                const active = statusFilter === st.value
                return (
                  <button
                    key={st.value}
                    type="button"
                    onClick={() => setStatusFilter(st.value)}
                    className={`py-2 text-center rounded-xl text-[12.5px] font-bold transition-all border ${
                      active
                        ? 'bg-[#056b4f] text-white border-[#056b4f] shadow-sm'
                        : 'bg-[#f8fafc] text-[#64748b] border-[#e2e8f0] active:bg-[#f1f5f9]'
                    }`}
                  >
                    {st.label}
                  </button>
                )
              })}
            </div>
          </div>
        </div>

        {/* 2. 导出格式模板选择 (与 App 3大模板卡片完全对齐) */}
        <div className="space-y-2">
          <div className="text-[13px] font-bold text-[#0f172a] px-1">
            选择导出报表格式
          </div>

          <div className="space-y-2.5">
            {TEMPLATE_OPTIONS.map(tpl => {
              const active = selectedTemplate === tpl.type
              return (
                <div
                  key={tpl.type}
                  onClick={() => setSelectedTemplate(tpl.type)}
                  className={`bg-white rounded-2xl p-4 border transition-all cursor-pointer relative ${
                    active
                      ? 'border-[#056b4f] shadow-[0_4px_16px_rgba(5,107,79,0.12)] ring-2 ring-[#056b4f]/20'
                      : 'border-[#e2e8f0] shadow-sm'
                  }`}
                >
                  <div className="flex items-center justify-between mb-1.5">
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-0.5 rounded text-[11px] font-bold ${tpl.tagColor}`}>
                        {tpl.badge}
                      </span>
                      <span className="text-[15px] font-bold text-[#0f172a]">{tpl.title}</span>
                    </div>

                    <div className="flex items-center gap-2">
                      <span className="text-[11px] font-bold text-[#056b4f] bg-[#f0fdf4] px-2 py-0.5 rounded-full border border-[#a7f3d0]">
                        {tpl.recommend}
                      </span>
                      <div
                        className={`w-5 h-5 rounded-full border flex items-center justify-center ${
                          active
                            ? 'bg-[#056b4f] border-[#056b4f] text-white'
                            : 'border-[#cbd5e1] bg-white'
                        }`}
                      >
                        {active && (
                          <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                            <polyline points="20 6 9 17 4 12"></polyline>
                          </svg>
                        )}
                      </div>
                    </div>
                  </div>

                  <p className="text-[12px] text-[#64748b] leading-relaxed pl-1">
                    {tpl.desc}
                  </p>
                </div>
              )
            })}
          </div>
        </div>

        {/* 3. 动态数据概览卡片 */}
        <div className="bg-white rounded-2xl p-4 shadow-sm border border-[#e2e8f0]">
          <div className="flex items-center justify-between pb-3 mb-3 border-b border-[#f1f5f9]">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-[#056b4f]"></div>
              <span className="text-[14px] font-bold text-[#0f172a]">拟导出数据概览</span>
            </div>
            <span className="text-[12px] text-[#056b4f] font-semibold">
              共计 {totalCount} 名学生
            </span>
          </div>

          <div className="grid grid-cols-3 gap-2 text-center">
            <div className="p-2 rounded-xl bg-[#f8fafc] border border-[#f1f5f9]">
              <div className="text-[11px] text-[#94a3b8]">平均分</div>
              <div className="text-[16px] font-extrabold text-[#0f172a] mt-0.5">{avgScore}</div>
            </div>
            <div className="p-2 rounded-xl bg-[#f8fafc] border border-[#f1f5f9]">
              <div className="text-[11px] text-[#94a3b8]">优良率</div>
              <div className="text-[16px] font-extrabold text-[#00b578] mt-0.5">{excellentRate}</div>
            </div>
            <div className="p-2 rounded-xl bg-[#f8fafc] border border-[#f1f5f9]">
              <div className="text-[11px] text-[#94a3b8]">及格率</div>
              <div className="text-[16px] font-extrabold text-[#fa8c16] mt-0.5">{passRate}</div>
            </div>
          </div>

          {/* 实时预览数据列表 (前 15 名) */}
          <div className="mt-4 pt-3 border-t border-[#f1f5f9]">
            <div className="flex items-center justify-between text-[11.5px] font-semibold text-[#64748b] mb-2">
              <span>名单实时预览 (显示前 15 条)</span>
              <span>{loadingPreview ? '更新中...' : '已与后台同步'}</span>
            </div>

            <div className="max-h-48 overflow-y-auto divide-y divide-[#f1f5f9] text-[12px]">
              {previewResult.data.slice(0, 15).map((stu, i) => (
                <div key={stu.id || i} className="py-2 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-[11px] text-[#94a3b8] font-bold w-4">{i + 1}</span>
                    <span className="font-bold text-[#0f172a]">{stu.student_name || '学生'}</span>
                    <span className="text-[11px] text-[#64748b]">{stu.class_name}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-[#0f172a]">
                      {stu.composite_score ?? stu.total_score ?? '未测'}
                    </span>
                    <span
                      className={`px-1.5 py-0.2 rounded text-[10.5px] font-bold ${
                        stu.grade_level === 'A'
                          ? 'bg-[#e8f9f0] text-[#00b578]'
                          : stu.grade_level === 'B'
                          ? 'bg-[#e8f6f0] text-[#056b4f]'
                          : stu.grade_level === 'C'
                          ? 'bg-[#fff7e6] text-[#fa8c16]'
                          : 'bg-[#fef2f2] text-[#e11d48]'
                      }`}
                    >
                      {stu.grade_level || '待测'}
                    </span>
                  </div>
                </div>
              ))}
              {previewResult.data.length === 0 && (
                <div className="py-6 text-center text-[#94a3b8] text-[12px]">
                  {loadingPreview ? '正在检索匹配学生名单...' : '暂无符合筛选条件的学生'}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* 底部固定操作栏 */}
      <div className="fixed bottom-0 left-0 right-0 p-4 bg-white/95 backdrop-blur-md border-t border-[#e2e8f0] z-40 max-w-lg mx-auto">
        <button
          type="button"
          disabled={exporting || totalCount === 0}
          onClick={handleDownload}
          className={`w-full h-12 rounded-full font-bold text-[15.5px] text-white flex items-center justify-center gap-2 transition-all shadow-md ${
            exporting || totalCount === 0
              ? 'bg-[#94a3b8] cursor-not-allowed'
              : 'bg-gradient-to-r from-[#056b4f] to-[#04523c] active:opacity-90 active:scale-[0.99]'
          }`}
        >
          {exporting ? (
            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
          ) : (
            <>
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                <polyline points="7 10 12 15 17 10"></polyline>
                <line x1="12" y1="15" x2="12" y2="3"></line>
              </svg>
              <span>一键导出并下载 Excel 表格 (.xlsx)</span>
            </>
          )}
        </button>
      </div>

      {/* 学年选择弹窗 */}
      <Popup
        visible={showYearPicker}
        position="bottom"
        round
        onClose={() => setShowYearPicker(false)}
      >
        <Picker
          title="选择学年"
          columns={academicYears.map(y => ({ text: `${y} 学年`, value: y }))}
          onConfirm={(val) => {
            setSelectedYear(val)
            localStorage.setItem('current_academic_year', val)
            setShowYearPicker(false)
          }}
          onCancel={() => setShowYearPicker(false)}
        />
      </Popup>

      {/* 适用年级选择弹窗 */}
      <Popup
        visible={showGradePicker}
        position="bottom"
        round
        onClose={() => setShowGradePicker(false)}
      >
        <Picker
          title="选择适用年级"
          columns={GRADE_OPTIONS.map(g => ({ text: g, value: g }))}
          onConfirm={(val) => {
            handleGradeSelect(val)
            setShowGradePicker(false)
          }}
          onCancel={() => setShowGradePicker(false)}
        />
      </Popup>

      {/* 限定班级选择弹窗 */}
      <Popup
        visible={showClassPicker}
        position="bottom"
        round
        onClose={() => setShowClassPicker(false)}
      >
        <Picker
          title="选择限定班级"
          columns={[
            { text: '全部班级', value: '' },
            ...filteredClasses.map(c => ({ text: c.class_name, value: String(c.id) }))
          ]}
          onConfirm={(val) => {
            setSelectedClassId(val ? Number(val) : null)
            setShowClassPicker(false)
          }}
          onCancel={() => setShowClassPicker(false)}
        />
      </Popup>
    </div>
  )
}
